import * as React from 'react';
import { Role } from '../contants';

export type Viewer = {
  role: string;
  storeId?: string;
  brandId?: string;
  roleSetManually?: boolean;
  eventIds?: string[];
};

export function CanAccessPartnerData(viewer: Viewer) {
  const roles = [
    Role.administrator,
    Role.accountManager,
    Role.finance,
    Role.marketing,
    Role.operations,
    Role.support,
    Role.eventManager,
    Role.eventOwner,
  ];
  return roles.includes(viewer?.role);
}

/**
 * Mirrors the backend's `Claims.PartnerDataAccess`
 * (backend/dotnet/VytalModel/User/Claims.cs) — the claim set that
 * `[AuthorizeClaims(Claims.PartnerDataAccess)]` endpoints actually accept.
 * Deliberately narrower than CanAccessPartnerData above, which also admits the
 * two event roles and therefore does not predict what those endpoints allow.
 */
export function HasPartnerDataClaim(viewer: Viewer) {
  const roles = [
    Role.administrator,
    Role.accountManager,
    Role.finance,
    Role.marketing,
    Role.operations,
    Role.support,
  ];
  return roles.includes(viewer?.role);
}

/**
 * Mirrors colugo's `canAccessAssetInternalComment`
 * (backend/colugo/utils/MiddleWares/Admin.js) — the role list behind the
 * `@assetInternalCommentAuth` directive and behind the write path in
 * StoreAsset.create/update. Wider than HasPartnerDataClaim above by
 * eventManager, and narrower than CanAccessPartnerData by eventOwner, which is
 * an external role that must neither read nor write the note. Keep the two
 * lists in step: a role that sees the field here but not there gets an input
 * whose content the backend silently drops.
 */
export function CanAccessAssetInternalComment(viewer: Viewer) {
  const roles = [
    Role.administrator,
    Role.accountManager,
    Role.support,
    Role.operations,
    Role.finance,
    Role.marketing,
    Role.eventManager,
  ];
  return roles.includes(viewer?.role);
}

export function CanAccessContainerStation(viewer: Viewer) {
  const roles = [
    Role.administrator,
    Role.accountManager,
    Role.operations,
    Role.eventCleaningEmployee,
    Role.eventManager,
  ];
  return roles.includes(viewer?.role);
}

export function CanAccessItemLocation(viewer: Viewer) {
  const roles = [
    Role.administrator,
    Role.accountManager,
    Role.operations,
    Role.eventCleaningEmployee,
    Role.eventManager,
    Role.eventOwner,
  ];
  return roles.includes(viewer?.role);
}

export function CanAccessBills(viewer: Viewer) {
  const roles = [Role.administrator, Role.finance, Role.support];
  return roles.includes(viewer?.role);
}

export function IsFinance(viewer: Viewer) {
  const roles = [Role.administrator, Role.finance];
  return roles.includes(viewer?.role);
}

export function IsHumanResources(viewer: Viewer) {
  const roles = [Role.administrator, Role.humanResources];
  return roles.includes(viewer?.role);
}

export function CanAccessUserData(viewer: Viewer) {
  const roles = [Role.administrator, Role.support, Role.finance];
  return roles.includes(viewer?.role);
}

export function CanAccessOperationsData(viewer: Viewer) {
  const roles = [Role.administrator, Role.operations];
  return roles.includes(viewer?.role);
}

/**
 * Who may open the picking job pages. Mirrors colugo's
 * PICKING_JOB_PRIVILEGED_ROLES (schema/Picking/utils/auth.ts): every other
 * role gets refused by the backend, so showing them the page would only show
 * them an error. Keep the two lists in step.
 */
export function CanAccessPickingJobs(viewer: Viewer) {
  const roles = [
    Role.administrator,
    Role.accountManager,
    Role.eventManager,
    Role.operations,
    Role.support,
  ];
  return roles.includes(viewer?.role);
}

export function IsMarketing(viewer: Viewer) {
  const roles = [Role.administrator, Role.marketing];
  return roles.includes(viewer?.role);
}

export function IsVytalEmployee(viewer: Viewer) {
  const roles = [
    Role.administrator,
    Role.accountManager,
    Role.finance,
    Role.marketing,
    Role.operations,
    Role.support,
    Role.humanResources,
    Role.eventManager,
  ];

  return roles.includes(viewer?.role);
}

export function IsAdmin(viewer: Viewer) {
  const roles = [Role.administrator];
  return roles.includes(viewer?.role);
}

export function CanAccessEventOverview(viewer: Viewer) {
  const roles = [
    Role.administrator,
    Role.eventCleaningEmployee,
    Role.finance,
    Role.operations,
  ];
  return roles.includes(viewer?.role);
}

export function IsEventAccountUser(viewer: Viewer) {
  const roles = [Role.eventAccountUser, Role.eventManager, Role.eventOwner];
  return roles.includes(viewer?.role);
}

export function IsEventManagerClaim(viewer: Viewer) {
  const roles = [Role.eventManager];

  return roles.includes(viewer?.role);
}

export function IsEventCleaningEmployeeClaim(viewer: Viewer) {
  const roles = [Role.eventCleaningEmployee];

  return roles.includes(viewer?.role);
}

export function IsEventOwnerClaim(viewer: Viewer) {
  const roles = [Role.eventOwner];

  return roles.includes(viewer?.role);
}

export function IsVytalEmployeeClaim(claim?: string | null) {
  if (claim == null) {
    return false;
  }
  const claims = [
    'human_resources',
    'account_manager',
    'operations',
    'marketing',
    'support',
    'finance',
    'iadm',
    'isadm',
  ];
  return claims.includes(claim);
}

export function IsVytalEmployeeClaimOrMerchantClaim(claim?: string | null) {
  if (claim == null) {
    return false;
  }
  const claims = [
    'human_resources',
    'account_manager',
    'operations',
    'marketing',
    'support',
    'finance',
    'iadm',
    'isadm',
    'imac',
  ];
  return claims.includes(claim);
}

export function IsSelectableClaim(claim?: string | null) {
  if (claim == null) {
    return false;
  }
  const claims = [
    'human_resources',
    'account_manager',
    'operations',
    'marketing',
    'support',
    'finance',
    'iadm',
    'imac',
    'event_manager',
    'event_owner',
    'event_cleaning_employee',
  ];
  return claims.includes(claim);
}

export function canChangeVytalUserClaims(viewer: Viewer) {
  const roles = [Role.administrator, Role.humanResources];
  return roles.includes(viewer?.role);
}

export const ViewerContext = React.createContext<Viewer>({ role: '' });
export const UpdateViewerContext = React.createContext<
  (viewer: Viewer) => void
>(() => {});
